import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { dealerResources, faqs } from '../mock';
import { 
  Download,
  FileText,
  Image,
  Shield,
  Lock,
  Search,
  ChevronDown,
  Users,
  TrendingUp,
  Award,
  Recycle,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  Package,
  Truck,
  BarChart,
  Handshake,
  Target,
  ArrowRight,
  Leaf
} from 'lucide-react';

const DealerHub = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const filteredResources = dealerResources.filter(resource =>
    resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const dealerBenefits = [
    {
      icon: TrendingUp,
      title: "Better Profit Margins",
      description: "Earn 2-3x more profit compared to big brand competitors",
      details: [
        "Competitive wholesale pricing",
        "No import tariffs or fees",
        "Volume discounts available",
        "Exclusive dealer pricing tiers"
      ]
    },
    {
      icon: Truck,
      title: "Faster Delivery",
      description: "Domestic production means no overseas delays",
      details: [
        "1-2 week standard turnaround",
        "No customs complications",
        "Rush orders available",
        "Predictable delivery schedules"
      ]
    },
    {
      icon: Users,
      title: "Partnership Support",
      description: "Real humans who understand your business",
      details: [
        "Dedicated account managers",
        "24-hour response guarantee",
        "Custom solutions development",
        "Marketing support included"
      ]
    },
    {
      icon: Award,
      title: "Quality Guarantee",
      description: "40+ years of proven performance and reliability",
      details: [
        "Comprehensive product warranties",
        "Quality control testing",
        "Customer satisfaction guarantee",
        "Defect replacement program"
      ]
    }
  ];

  const marketingTools = [
    {
      icon: Image,
      title: "High-Resolution Product Images",
      description: "Professional product photography for your marketing",
      type: "Image Library"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Enhanced Hero Section */}
      <section className="bg-white py-20 overflow-hidden relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" viewBox="0 0 100 100" fill="none">
            <defs>
              <pattern id="dealer-grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#dealer-grid)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <Badge className="bg-automats-eco-green/10 text-automats-eco-green border-automats-eco-green/20 px-6 py-2">
                <Handshake className="h-4 w-4 mr-2" />
                Resource Hub
              </Badge>
              <div className="space-y-6">
                <h1 className="text-6xl lg:text-7xl font-heading font-bold text-automats-black leading-tight">
                  Your Success is{' '}
                  <span className="text-automats-red">Our Success</span>
                </h1>
                <p className="text-2xl font-body text-automats-steel leading-relaxed">
                  Access exclusive resources, premium pricing, and marketing materials 
                  designed to help AutoMats dealers dominate their market and maximize profits.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-6">
                <a 
                  href="mailto:sales@automats.us?subject=Become a Dealer&body=Hello, I would like to learn more about becoming an AutoMats dealer."
                  className="bg-automats-red hover:bg-red-700 text-white px-10 py-4 rounded-xl font-body font-bold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  <Users className="mr-2 h-5 w-5 inline" />
                  Become a Dealer
                </a>
                <a 
                  href="mailto:sales@automats.us?subject=Dealer Support Request&body=Hello, I need support as an AutoMats dealer."
                  className="border-2 border-automats-steel text-automats-steel hover:bg-automats-steel hover:text-white px-10 py-4 rounded-xl font-body font-bold text-lg transition-all duration-300 hover:scale-105"
                >
                  <Phone className="mr-2 h-5 w-5 inline" />
                  Contact Support
                </a>
              </div>
            </div>

            {/* Enhanced Success Metrics */}
            <div className="bg-gradient-to-br from-automats-off-white to-white rounded-3xl p-12 shadow-2xl border border-gray-100">
              <h3 className="text-3xl font-heading font-bold mb-8 text-center text-automats-black">Dealer Success Metrics</h3>
              <div className="space-y-8">
                <div className="text-center">
                  <div className="text-6xl font-heading font-bold text-automats-red mb-2">2-3x</div>
                  <div className="text-lg font-body text-automats-steel">Higher Profit Margins</div>
                  <div className="text-sm font-body text-automats-steel mt-1">vs. big brand competitors</div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-heading font-bold text-automats-eco-green mb-2">40+</div>
                    <div className="text-sm font-body text-automats-steel">Years Experience</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-heading font-bold text-automats-steel mb-2">100%</div>
                    <div className="text-sm font-body text-automats-steel">Recycled Materials</div>
                  </div>
                </div>
                <div className="text-center bg-automats-red/10 p-4 rounded-xl">
                  <div className="text-lg font-heading font-semibold text-automats-black">Made in Dalton, GA</div>
                  <div className="text-sm font-body text-automats-steel">Fast, reliable domestic delivery</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <Tabs defaultValue="resources" className="space-y-12">
          <TabsList className="grid w-full grid-cols-4 bg-automats-off-white p-2 rounded-xl">
            <TabsTrigger value="resources" className="font-body font-semibold">Resources</TabsTrigger>
            <TabsTrigger value="benefits" className="font-body font-semibold">Benefits</TabsTrigger>
            <TabsTrigger value="marketing" className="font-body font-semibold">Marketing</TabsTrigger>
            <TabsTrigger value="support" className="font-body font-semibold">Support</TabsTrigger>
          </TabsList>

          {/* Resources Tab */}
          <TabsContent value="resources" className="space-y-8">
            {/* Search */}
            <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-8">
              <div className="flex items-center space-x-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search resources..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 py-3 text-lg"
                  />
                </div>
                <a 
                  href="mailto:sales@automats.us?subject=Download All Resources&body=Hello, I would like to download all dealer resources."
                  className="bg-automats-red hover:bg-red-700 text-white px-6 py-3 rounded-lg font-body font-semibold transition-all duration-300 hover:scale-105"
                >
                  <Download className="h-4 w-4 mr-2 inline" />
                  Download All
                </a>
              </div>
            </div>

            {/* Resource Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredResources.map(resource => (
                <Card key={resource.id} className="hover:shadow-2xl transition-all duration-300 cursor-pointer group border border-gray-100">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-xl font-heading group-hover:text-automats-red transition-colors">
                          {resource.title}
                        </CardTitle>
                        <CardDescription className="mt-3 text-base font-body">
                          {resource.description}
                        </CardDescription>
                      </div>
                      <div className="ml-4">
                        {resource.type === 'PDF' && <FileText className="h-10 w-10 text-automats-red" />}
                        {resource.type === 'ZIP' && <Package className="h-10 w-10 text-automats-steel" />}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Badge variant="secondary">{resource.type}</Badge>
                        <span>{resource.size}</span>
                      </div>
                      {resource.title === "Product Catalog 2025" ? (
                        <a 
                          href="/images/AutoMats_Brochure.pdf"
                          download="AutoMats_Product_Catalog.pdf"
                          className="bg-automats-red hover:bg-red-700 text-white px-4 py-2 rounded-lg font-body font-semibold transition-all duration-300 hover:scale-105"
                        >
                          <Download className="h-4 w-4" />
                        </a>
                      ) : (
                        <a 
                          href="mailto:sales@automats.us?subject=Resource Download Request&body=Hello, I would like to download the resource."
                          className="bg-automats-red hover:bg-red-700 text-white px-4 py-2 rounded-lg font-body font-semibold transition-all duration-300 hover:scale-105"
                        >
                          <Download className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Enhanced Eco Impact Calculator Access */}
            <div className="bg-gradient-to-br from-automats-eco-green via-green-500 to-green-600 rounded-3xl p-12 text-white shadow-2xl relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <svg className="w-full h-full" viewBox="0 0 100 100" fill="none">
                  <defs>
                    <pattern id="eco-grid" width="10" height="10" patternUnits="userSpaceOnUse">
                      <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
                    </pattern>
                  </defs>
                  <rect width="100" height="100" fill="url(#eco-grid)" />
                </svg>
              </div>
              
              <div className="relative z-10">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div className="space-y-8">
                    <div className="space-y-4">
                      <h3 className="text-5xl font-heading font-bold text-white">
                        Environmental Impact Calculator
                      </h3>
                      <p className="text-xl text-green-100 leading-relaxed">
                        Show your customers the real environmental benefits of choosing AutoMats. 
                        Demonstrate plastic saved and CO₂ reduced with every purchase.
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-6">
                      <div className="bg-white/20 p-6 rounded-xl text-center backdrop-blur-sm">
                        <div className="text-4xl font-heading font-bold text-white mb-2">2.0 lbs</div>
                        <div className="text-sm text-green-100">Plastic Saved Per Mat</div>
                      </div>
                      <div className="bg-white/20 p-6 rounded-xl text-center backdrop-blur-sm">
                        <div className="text-4xl font-heading font-bold text-white mb-2">3.6 lbs</div>
                        <div className="text-sm text-green-100">CO₂ Saved Per Mat</div>
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <a 
                        href="/impact"
                        className="bg-white text-automats-eco-green hover:bg-gray-100 px-8 py-4 rounded-xl font-body font-bold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
                      >
                        Use Calculator
                      </a>
                    </div>
                  </div>
                  
                  {/* Visual Element */}
                  <div className="text-center">
                    <div className="bg-white/20 w-40 h-40 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl backdrop-blur-sm">
                      <Leaf className="h-20 w-20 text-white" />
                    </div>
                    <h4 className="text-2xl font-heading font-bold text-white mb-3">100% Recycled</h4>
                    <p className="text-lg text-green-100">Zero virgin materials used</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Benefits Tab */}
          <TabsContent value="benefits" className="space-y-12">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-heading font-bold text-automats-black mb-6">
                Why Choose AutoMats Partnership?
              </h2>
              <p className="text-xl font-body text-automats-steel max-w-4xl mx-auto leading-relaxed">
                Join a network of successful dealers who've discovered the competitive 
                advantages that come with partnering with AutoMats.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-10">
              {dealerBenefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="hover:shadow-2xl transition-all duration-300 border border-gray-100">
                    <CardHeader className="pb-6">
                      <div className="flex items-center space-x-6">
                        <div className="bg-automats-red/10 p-4 rounded-full shadow-lg">
                          <IconComponent className="h-10 w-10 text-automats-red" />
                        </div>
                        <div>
                          <CardTitle className="text-2xl font-heading">{benefit.title}</CardTitle>
                          <CardDescription className="text-lg font-body mt-2">
                            {benefit.description}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-4">
                        {benefit.details.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-center space-x-4">
                            <ArrowRight className="w-5 h-5 text-automats-red flex-shrink-0" />
                            <span className="text-lg font-body text-automats-steel">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Profit Margin Calculator removed as requested */}
          </TabsContent>

          {/* Marketing Tab */}
          <TabsContent value="marketing" className="space-y-12">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-heading font-bold text-automats-black mb-6">
                Marketing Tools & Materials
              </h2>
              <p className="text-xl font-body text-automats-steel max-w-4xl mx-auto leading-relaxed">
                Professional marketing materials to help you sell more effectively 
                and communicate the AutoMats value proposition.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-10">
              {marketingTools.map((tool, index) => {
                const IconComponent = tool.icon;
                return (
                  <Card key={index} className="hover:shadow-2xl transition-all duration-300 border border-gray-100">
                    <CardHeader className="pb-6">
                      <div className="flex items-center space-x-6">
                        <div className="bg-automats-steel/10 p-4 rounded-full shadow-lg">
                          <IconComponent className="h-8 w-8 text-automats-steel" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-xl font-heading">{tool.title}</CardTitle>
                          <Badge variant="secondary" className="mt-2">
                            {tool.type}
                          </Badge>
                        </div>
                      </div>
                      <CardDescription className="text-lg font-body mt-4">
                        {tool.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {tool.title === "High-Resolution Product Images" ? (
                        <a 
                          href="https://drive.google.com/drive/folders/17h14gvXLJ2qIwTuY3AnfG95ReJFWLFvl?usp=share_link"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full bg-automats-red hover:bg-red-700 text-white px-6 py-3 rounded-lg font-body font-semibold transition-all duration-300 hover:scale-105 text-center block"
                        >
                          <Download className="mr-2 h-4 w-4 inline" />
                          Access Drive
                        </a>
                      ) : (
                        <a 
                          href="mailto:sales@automats.us?subject=Marketing Material Request&body=Hello, I would like to download marketing materials."
                          className="w-full bg-automats-red hover:bg-red-700 text-white px-6 py-3 rounded-lg font-body font-semibold transition-all duration-300 hover:scale-105 text-center block"
                        >
                          <Download className="mr-2 h-4 w-4 inline" />
                          Download
                        </a>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Enhanced Co-branding */}
            <Card className="bg-gradient-to-br from-automats-steel/10 to-gray-50 border border-automats-steel/20 shadow-2xl">
              <CardHeader className="pb-8">
                <CardTitle className="text-4xl font-heading font-bold text-automats-black">Co-Branding Opportunities</CardTitle>
                <CardDescription className="text-xl font-body text-automats-steel mt-4">
                  Add your logo and branding to AutoMats products
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-heading font-bold text-xl mb-4 text-automats-black">Available Options:</h4>
                    <ul className="space-y-3 font-body text-lg text-automats-steel">
                      <li className="flex items-center">
                        <ArrowRight className="h-5 w-5 text-automats-red mr-3" />
                        Custom logo placement on mats
                      </li>
                      <li className="flex items-center">
                        <ArrowRight className="h-5 w-5 text-automats-red mr-3" />
                        Private label packaging
                      </li>
                      <li className="flex items-center">
                        <ArrowRight className="h-5 w-5 text-automats-red mr-3" />
                        Custom color combinations
                      </li>
                      <li className="flex items-center">
                        <ArrowRight className="h-5 w-5 text-automats-red mr-3" />
                        Branded hang tags and displays
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-xl mb-4 text-automats-black">Benefits:</h4>
                    <ul className="space-y-3 font-body text-lg text-automats-steel">
                      <li className="flex items-center">
                        <ArrowRight className="h-5 w-5 text-automats-eco-green mr-3" />
                        Strengthen your brand presence
                      </li>
                      <li className="flex items-center">
                        <ArrowRight className="h-5 w-5 text-automats-eco-green mr-3" />
                        Build customer loyalty
                      </li>
                      <li className="flex items-center">
                        <ArrowRight className="h-5 w-5 text-automats-eco-green mr-3" />
                        Command premium pricing
                      </li>
                      <li className="flex items-center">
                        <ArrowRight className="h-5 w-5 text-automats-eco-green mr-3" />
                        Differentiate from competitors
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="mt-8 text-center">
                  <a 
                    href="mailto:sales@automats.us?subject=Co-Branding Information Request&body=Hello, I would like to learn more about co-branding opportunities."
                    className="bg-automats-steel hover:bg-gray-600 text-white px-10 py-4 rounded-xl font-body font-bold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
                  >
                    Request Co-Branding Info
                  </a>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support" className="space-y-12">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-heading font-bold text-automats-black mb-6">
                Dealer Support & FAQ
              </h2>
              <p className="text-xl font-body text-automats-steel max-w-4xl mx-auto leading-relaxed">
                Get the help you need when you need it. Our support team is here 
                to ensure your success as an AutoMats dealer.
              </p>
            </div>

            {/* Enhanced Contact Options */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <Card className="text-center hover:shadow-2xl transition-all border border-gray-100">
                <CardContent className="p-8">
                  <Phone className="h-16 w-16 text-automats-red mx-auto mb-6" />
                  <h3 className="text-2xl font-heading font-bold mb-3 text-automats-black">Phone Support</h3>
                  <p className="text-xl font-body text-automats-steel mb-4">800-570-0704</p>
                  <p className="text-lg font-body text-automats-steel">Mon-Fri: 8AM-5PM EST</p>
                </CardContent>
              </Card>
              
              <Card className="text-center hover:shadow-2xl transition-all border border-gray-100">
                <CardContent className="p-8">
                  <Mail className="h-16 w-16 text-automats-steel mx-auto mb-6" />
                  <h3 className="text-2xl font-heading font-bold mb-3 text-automats-black">Email Support</h3>
                  <p className="text-xl font-body text-automats-steel mb-4">sales@automats.us</p>
                  <p className="text-lg font-body text-automats-steel">24-hour response guarantee</p>
                </CardContent>
              </Card>
              
              <Card className="text-center hover:shadow-2xl transition-all border border-gray-100">
                <CardContent className="p-8">
                  <Calendar className="h-16 w-16 text-automats-eco-green mx-auto mb-6" />
                  <h3 className="text-2xl font-heading font-bold mb-3 text-automats-black">Schedule Call</h3>
                  <p className="text-xl font-body text-automats-steel mb-6">1-on-1 consultation</p>
                  <a 
                    href="mailto:sales@automats.us?subject=Schedule Consultation&body=Hello, I would like to schedule a 1-on-1 consultation."
                    className="bg-automats-eco-green hover:bg-green-600 text-white px-6 py-3 rounded-lg font-body font-semibold transition-all duration-300 hover:scale-105"
                  >
                    Book Now
                  </a>
                </CardContent>
              </Card>
            </div>

            {/* Enhanced FAQ Section */}
            <Card className="shadow-2xl border border-gray-100">
              <CardHeader className="pb-8">
                <CardTitle className="text-3xl font-heading font-bold text-automats-black">Frequently Asked Questions</CardTitle>
                <CardDescription className="text-lg font-body text-automats-steel mt-2">
                  Find answers to common questions about the AutoMats dealer program
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {faqs.map(faq => (
                    <div key={faq.id} className="border-b border-gray-200 pb-6">
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                        className="flex items-center justify-between w-full text-left py-3 hover:text-automats-red transition-colors"
                      >
                        <span className="font-heading font-semibold text-lg text-automats-black">{faq.question}</span>
                        <ChevronDown 
                          className={`h-6 w-6 text-automats-red transition-transform ${
                            expandedFaq === faq.id ? 'rotate-180' : ''
                          }`} 
                        />
                      </button>
                      {expandedFaq === faq.id && (
                        <div className="mt-4 text-lg font-body text-automats-steel leading-relaxed">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DealerHub;